import { Box, Divider, Stack, Typography } from '@mui/material';
import { DataGridPremium, GridColDef } from '@mui/x-data-grid-premium';
import { useAppTFunction } from '@retail/app/i18n';
import { ConditionsTableActions, LevelChip } from '@retail/calculus/components';
import { ConditionsNew } from '@retail/calculus/types';
import { AddButton, NoTableRowsOverlay, StackedTextCell } from '@retail/components';
import { getPriceSuggestionType } from '@retail/products/utils';
import { getDataGridLocalization, getLLDate, MgPrisLocale } from '@retail/utils';
import { useMemo } from 'react';
import { PriceSuggestionPriority } from '@retail/products/types';
import { useRowColorGrouping } from '@retail/hooks';

interface Props {
  loading: boolean;
  priceSuggestionConditions: ConditionsNew[];
  language: string;
  noPriceSuggestionTypes: boolean;
  supplierLevel: boolean;
  conditionActions?: {
    setEdit: (condition: ConditionsNew) => void;
    setDeactivate: (conditionId?: number) => void;
    setDelete: (conditionId?: number) => void;
  };

  openCreateDialog?: () => void;
}

const baseColumnProps: Partial<GridColDef> = {
  width: 140,
  sortable: false,
  headerAlign: 'left',
  align: 'left',
};

export function PriceSuggestionConditionsTable({
  loading,
  priceSuggestionConditions,
  language,
  noPriceSuggestionTypes,
  supplierLevel,
  conditionActions,
  openCreateDialog,
}: Props) {
  const t = useAppTFunction();
  const { getRowColorForStripedTable } = useRowColorGrouping();

  const columns: GridColDef<ConditionsNew>[] = useMemo(
    () => [
      {
        ...baseColumnProps,
        field: 'level',
        headerName: t('purchaseAgreements.agreement.columns.type'),
        width: 190,
        renderCell: ({ row }) => {
          return <LevelChip bindingType={row.bindingType} active={!row.inDraft} />;
        },
      },
      {
        ...baseColumnProps,
        field: 'supplier',
        headerName: t('markup.supplier'),
        width: 190,
        valueGetter: (_, row) => row.supplier?.supplierNumber,
        renderCell: ({ row }) =>
          row.supplier ? (
            <StackedTextCell topLine={row.supplier.name} bottomLine={row.supplier.supplierNumber} />
          ) : (
            '–'
          ),
      },
      {
        ...baseColumnProps,
        field: 'name',
        headerName: t('priceSuggestion.levelOrItemName'),
        width: 260,
        renderCell: ({ row }) => (
          <StackedTextCell topLine={row.bindingTargetName} bottomLine={row.bindingTargetId} />
        ),
      },
      {
        ...baseColumnProps,
        field: 'priceSuggestionPriority',
        headerName: t('priceSuggestion.priceType'),
        width: 220,
        renderCell: ({ row }) => {
          const priceSuggestionsPriority: PriceSuggestionPriority[] =
            row.priceSourceCondition?.priorities ?? [];
          return (
            <Stack flexDirection="column" gap={1}>
              {priceSuggestionsPriority.map(({ priority, priceSuggestionType }) => {
                return (
                  <Stack key={priceSuggestionType} gap={1}>
                    <Stack flexDirection="row" alignItems="center" gap={2}>
                      <Typography variant="subtitle2">{priority}.</Typography>
                      {getPriceSuggestionType(t, priceSuggestionType)}
                    </Stack>
                    {priority !== priceSuggestionsPriority.length && <Divider variant="middle" />}
                  </Stack>
                );
              })}
            </Stack>
          );
        },
      },
      {
        ...baseColumnProps,
        field: 'validFromTo',
        headerName: t('purchaseAgreements.agreement.columns.activeFromTo'),
        renderCell: ({ row }) => {
          const validFrom = getLLDate(row.priceSourceCondition?.validFrom) ?? '';
          const validTo = getLLDate(row.priceSourceCondition?.validTo);
          return <StackedTextCell topLine={validFrom} bottomLine={validTo} />;
        },
      },
      {
        ...baseColumnProps,
        field: 'actions',
        width: 90,
        headerName: '',
        renderCell: ({ row }) =>
          conditionActions ? (
            <ConditionsTableActions
              handleEdit={() => conditionActions.setEdit(row)}
              handleDeactivate={
                !row.inDraft
                  ? () => conditionActions.setDeactivate(row.priceSourceCondition!.conditionId)
                  : undefined
              }
              handleDelete={
                row.inDraft
                  ? () => conditionActions.setDelete(row.priceSourceCondition!.conditionId)
                  : undefined
              }
            />
          ) : null,
      },
    ],
    [conditionActions, t]
  );

  return (
    <Box sx={{ flexGrow: 1, position: 'relative' }}>
      <Box sx={{ position: 'absolute', inset: 0 }}>
        <DataGridPremium
          columns={columns}
          rows={priceSuggestionConditions}
          getRowId={(row) => row.priceSourceCondition!.conditionId}
          getRowClassName={getRowColorForStripedTable}
          sx={{ '& .MuiDataGrid-cell': { py: 1.5 } }}
          getRowHeight={() => 'auto'}
          loading={loading}
          columnVisibilityModel={{ supplier: !supplierLevel }}
          disableRowSelectionOnClick
          disableColumnResize
          disableColumnReorder
          disableColumnMenu
          hideFooter
          localeText={getDataGridLocalization(language as MgPrisLocale)}
          initialState={{ pinnedColumns: { right: ['actions'] } }}
          slots={{
            noRowsOverlay: () => (
              <NoTableRowsOverlay
                noRowsText={t('priceSuggestion.noActivePriceSuggestion')}
                addAction={
                  !noPriceSuggestionTypes &&
                  !!openCreateDialog && (
                    <AddButton onClick={openCreateDialog}>
                      {t('priceSuggestion.followPriceSuggestion')}
                    </AddButton>
                  )
                }
              />
            ),
          }}
        />
      </Box>
    </Box>
  );
}
